import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import type { MutableRefObject } from 'react';

import { Box } from '@opendoor/atoms/box';
import { useAuth } from '@opendoor/auth-fe/context';
import { rememberAccountShape } from '@opendoor/shared-fe/header/account-shape';
import {
  headerAccountState,
  headerLink,
  headerTransactionRows,
} from '@opendoor/shared-fe/header/adapter';
import { SiteHeader } from '@opendoor/shared-fe/header/header';
import type {
  HeaderAccountState,
  HeaderAppearance,
  HeaderLinkAction,
  HeaderLinkComponent,
  HeaderLinkProps,
  HeaderNavItem,
} from '@opendoor/shared-fe/header/model';
import {
  ACCOUNT_PATHS,
  HELP_CENTER_URL,
  siteNavigationItems,
} from '@opendoor/shared-fe/header/navigation';
import { getCustomerNavigationHref } from 'components/account/impersonation';
import { CONSUMER_URL, COSMOS_URL } from 'components/globals';
import { dashboardHref } from 'helpers/dashboardSession';
import { useObservability } from 'helpers/observability';
import {
  navTransactionLabel,
  useNavTransactions,
  usePrefetchNavTransactions,
} from 'helpers/useNavTransactions';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { SkipLink } from './SkipLink';

const CTA_LABEL = 'Get a cash offer';

function isDashboardSlug(slug: string): boolean {
  const normalizedSlug = slug.replace(/^\/+/, '');
  return normalizedSlug === 'dashboard' || normalizedSlug.startsWith('dashboard/');
}

const CosmosLink: HeaderLinkComponent = forwardRef<HTMLAnchorElement, HeaderLinkProps>(
  function CosmosLink(props, ref) {
    return <Link {...props} ref={ref} />;
  },
);

// A support session keeps the impersonated customer in the URL, so every header link carries it.
// The href resolves at render, like the Atoms links in the rest of Cosmos.
function customerHref(href: HeaderLinkAction['href']): HeaderLinkAction['href'] {
  return (placement) =>
    getCustomerNavigationHref(typeof href === 'function' ? href(placement) : href);
}

function cosmosLink(
  href: HeaderLinkAction['href'],
  navigation: HeaderLinkAction['navigation'],
  options?: Pick<HeaderLinkAction, 'onSelect'>,
): HeaderLinkAction {
  return headerLink(customerHref(href), navigation, options);
}

type TrackClick = (event: 'linkClickEvent' | 'buttonClickEvent', targetName: string) => void;

function useCosmosAccount(slug: string, track: TrackClick): HeaderAccountState {
  const { authentication, logout } = useAuth();
  useEffect(() => {
    if (authentication.state === 'authenticated') rememberAccountShape(true);
    if (authentication.state === 'not_authenticated') rememberAccountShape(false);
  }, [authentication.state]);
  const transactions = useNavTransactions();
  const { asPath, pathname } = useRouter();
  usePrefetchNavTransactions();

  const onAccountFormPage = pathname === '/login' || pathname.includes('/account');
  const signIn = onAccountFormPage
    ? null
    : {
        action: cosmosLink(
          `${COSMOS_URL}${ACCOUNT_PATHS.signIn}?redirectTo=${encodeURIComponent(asPath.split(/[?#]/)[0] ?? '/')}`,
          'document',
          {
            onSelect: (placement) => {
              track(
                'buttonClickEvent',
                placement === 'drawer' ? 'cosmos-mobile-menu-sign-in' : 'cosmos-sign-in',
              );
              if (placement === 'bar') track('buttonClickEvent', 'account-section-click');
            },
          },
        ),
      };

  return headerAccountState(authentication, {
    signIn,
    menu: () => ({
      dashboard: {
        action: cosmosLink(
          (placement) => dashboardHref(placement === 'drawer' ? 'mobile_menu' : 'nav'),
          'app',
          {
            onSelect: (placement) =>
              track(
                'linkClickEvent',
                placement === 'drawer'
                  ? 'cosmos-mobile-nav-dashboard'
                  : 'cosmos-mega-nav-dashboard',
              ),
          },
        ),
        current: isDashboardSlug(slug),
      },
      transactions: headerTransactionRows({
        transactions,
        label: navTransactionLabel,
        action: (transaction) =>
          cosmosLink(transaction.href, 'app', {
            onSelect: (placement) => {
              if (placement === 'drawer') {
                track('linkClickEvent', `cosmos-mobile-nav-${transaction.type}`);
              }
            },
          }),
      }),
      moreTransactions: cosmosLink(ACCOUNT_PATHS.properties, 'document'),
      transactionLinks: [],
      // Account belongs to the consumer app, so it always loads as a new document.
      account: cosmosLink(`${CONSUMER_URL}${ACCOUNT_PATHS.account}`, 'document'),
      helpCenter: cosmosLink(HELP_CENTER_URL, 'document', {
        onSelect: (placement) => {
          if (placement === 'drawer') track('linkClickEvent', 'cosmos-mobile-nav-help-center');
        },
      }),
      signOut: {
        kind: 'command',
        run: () => {
          track('buttonClickEvent', 'cosmos-account-menu-sign-out');
          logout();
        },
      },
      onTriggerPress: () => {
        track('buttonClickEvent', 'account-section-click');
        track('buttonClickEvent', 'user-account-menu-click');
      },
    }),
  });
}

interface StaticSiteHeaderProps {
  slug: string;
  /** Receives a function that closes the mobile drawer, for page controls that sit over it. */
  closeMenuRef?: MutableRefObject<() => void>;
  appearance?: Extract<HeaderAppearance, 'solid' | 'solid-secondary'>;
  contentClassName?: string;
}

const StaticSiteHeader = ({
  slug,
  closeMenuRef,
  appearance = 'solid',
  contentClassName,
}: StaticSiteHeaderProps) => {
  const { trackJourneyEvent } = useObservability();
  const track = useCallback<TrackClick>(
    (event, targetName) => trackJourneyEvent('platform', event, { targetName }),
    [trackJourneyEvent],
  );
  const account = useCosmosAccount(slug, track);
  const home = useMemo(
    () =>
      cosmosLink('/', 'document', {
        onSelect: (placement) =>
          track(
            'linkClickEvent',
            placement === 'drawer' ? 'cosmos-mobile-menu-home' : 'cosmos-logo-link',
          ),
      }),
    [track],
  );
  const navigation = useMemo<HeaderNavItem[]>(
    () =>
      siteNavigationItems({
        app: 'cosmos',
        hrefFor: (destination) => customerHref(destination.path),
        isCurrent: (destination) => slug === destination.path,
        onSelect: (destination, placement) =>
          track(
            'linkClickEvent',
            placement === 'drawer'
              ? `cosmos-mega-nav-mobile-${destination.label.toLowerCase()}`
              : `cosmos-landing-page-v2-nav-link-${destination.label}`,
          ),
      }),
    [slug, track],
  );
  const drawerCallToAction = useMemo(
    () => ({
      title: 'Get started',
      label: CTA_LABEL,
      action: cosmosLink('/', 'document', {
        onSelect: () =>
          track('linkClickEvent', `cosmos-landing-page-v2-menu-cta-link-${CTA_LABEL}`),
      }),
    }),
    [track],
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const mobileMenu = useMemo(() => ({ open: menuOpen, onOpenChange: setMenuOpen }), [menuOpen]);
  const handleMobileMenuToggle = useCallback(
    (open: boolean) => {
      if (open) track('buttonClickEvent', 'cosmos-landing-page-header-menu-button');
    },
    [track],
  );

  useEffect(() => {
    if (!closeMenuRef) return;
    closeMenuRef.current = () => setMenuOpen(false);
  }, [closeMenuRef]);

  return (
    <>
      <SkipLink />
      <Box className="sticky top-0 z-[2000] block">
        <SiteHeader
          appearance={appearance}
          contentClassName={contentClassName}
          linkComponent={CosmosLink}
          home={home}
          navigation={navigation}
          account={account}
          drawerCallToAction={drawerCallToAction}
          mobileMenu={mobileMenu}
          onMobileMenuToggle={handleMobileMenuToggle}
        />
      </Box>
    </>
  );
};

export default StaticSiteHeader;
